import { ChevronNav } from "@theme/assets";
import { useAtom, useSetAtom } from "jotai";
import { createRef, useEffect, useRef } from "react";

import {
  Box,
  Container,
  Flex,
  IconButton,
  NavDesktopCategoryDropdown,
  NavLink,
  NavSearchButton,
  Text,
  activeNavDropdownAtom,
  getThemeValue,
  navDesktopDropdownOpenAtom,
} from "@theme/components";

import type { NavCategory } from "@theme/types";
import type { FC, RefObject } from "react";

interface NavDesktopCategoryBarProps {
  navCategories?: NavCategory[];
}

export const NavDesktopCategoryBar: FC<NavDesktopCategoryBarProps> = ({ navCategories = [] }) => {
  const [activeTabIndex, setActiveTabIndex] = useAtom(activeNavDropdownAtom);

  const tabRefs = useRef(
    Array(navCategories.length)
      .fill(null)
      .map(() => createRef<HTMLElement>()),
  );

  const selectedNavCategory = navCategories[activeTabIndex];

  const showNavigation = (index: number) => {
    setActiveTabIndex(index);
  };

  const closeNavigation = () => {
    setActiveTabIndex(-1);
  };

  /**
   * Handle mouse enter/leave. when mouse enters or leaves the dropdown, set timeout for 250ms
   * If mouse continues to hover over the dropdown, open it.
   * If mouse has not re-entered the dropdown, close it.
   * This solves issues with mouse entering or leaving the dropdown when navigating on the page.
   */
  const openTimer = useRef<number | null>(null);
  const closeTimer = useRef<number | null>(null);

  const handleMouseEnter = (index: number) => {
    closeTimer.current && clearTimeout(closeTimer.current);
    // When the dropdown is already open, don't delay the next dropdown
    // Otherwise, set a delay to open the dropdown
    if (activeTabIndex !== index && activeTabIndex > -1) {
      showNavigation(index);
    } else {
      openTimer.current = window.setTimeout(() => {
        showNavigation(index);
      }, 400);
    }
  };
  const handleMouseLeave = () => {
    openTimer.current && clearTimeout(openTimer.current);

    closeTimer.current = window.setTimeout(() => {
      closeNavigation();
    }, 400);
  };

  useEffect(
    () => () => {
      openTimer.current && clearTimeout(openTimer.current);
      closeTimer.current && clearTimeout(closeTimer.current);
    },
    [],
  );

  // Set the navDesktopDropdownOpenAtom based on the activeTabIndex
  // this will add the underlay to the site when the dropdown is open
  const setNavDesktopDropdownOpen = useSetAtom(navDesktopDropdownOpenAtom);
  useEffect(() => {
    setNavDesktopDropdownOpen(activeTabIndex >= 0);
  }, [activeTabIndex, setNavDesktopDropdownOpen]);

  return (
    <Box
      sx={{
        // show only on desktop
        display: ["none", null, null, "flex"],
        borderBottom: "1px solid",
        borderBottomColor: "grey100",
        width: "full",
        bg: "beige100",
        zIndex: -2,
        height: (t) => getThemeValue(t, "sizes.categoryBarHeight"),
      }}
      onMouseLeave={handleMouseLeave}
    >
      <Container
        as="ul"
        role="menubar"
        variant="wide"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          height: "full",
          "& li div > button:has(svg):last-of-type": {
            mr: (t) => `-${getThemeValue(t, "space.s200")}`,
          },
        }}
      >
        {navCategories.map((navCategory, index) => {
          const isActive = activeTabIndex === index;

          return (
            <Flex
              as="li"
              role="none"
              onMouseEnter={() => {
                !navCategory.isStaticLink && handleMouseEnter(index);
              }}
              key={navCategory.slug}
              sx={{
                listStyleType: "none",
                m: 0,
                p: 0,
                mt: "3px",
                borderBottom: "3px solid",
                borderColor: isActive ? "goldDark" : "transparent",
                alignItems: "center",
                height: "full",
              }}
            >
              {/* Shop All Category we treat as a link */}
              {navCategory.isStaticLink ? (
                <NavLink
                  variant="box"
                  data-testid={`desktop-nav-${navCategory.slug}`}
                  sx={{
                    variant: "text.sansMicro02MediumPlus",
                    color: isActive ? "goldDark" : "grey700",
                    px: "s200",
                    textAlign: "center",
                  }}
                  ref={tabRefs.current[index] as RefObject<HTMLAnchorElement>}
                  role="menuitem"
                  to={navCategory.pageSlug}
                  tabIndex={0}
                  onClick={closeNavigation}
                >
                  {navCategory.title}
                </NavLink>
              ) : (
                <Flex sx={{ alignItems: "center", height: "full" }}>
                  <NavLink
                    variant="box"
                    data-testid={`desktop-nav-${navCategory.slug}`}
                    sx={{
                      variant: "text.sansMicro02MediumPlus",
                      display: "flex",
                      color: isActive ? "goldDark" : "grey700",
                      textAlign: "center",
                      height: "full",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    role="menuitem"
                    to={navCategory.pageSlug}
                    tabIndex={0}
                    onClick={closeNavigation}
                  >
                    <Text sx={{ font: "inherit" }}>{navCategory.title}</Text>
                  </NavLink>
                  <IconButton
                    data-testid={`desktop-nav-${navCategory.slug}`}
                    variant="ghost"
                    ref={tabRefs.current[index] as RefObject<HTMLButtonElement>}
                    role="menuitem"
                    aria-label={`${navCategory.title} submenu`}
                    aria-controls={`menu-${index}`}
                    aria-expanded={isActive}
                    onClick={() => (isActive ? closeNavigation() : showNavigation(index))}
                    sx={{
                      color: isActive ? "goldDark" : "grey700",
                      width: "auto",
                      transform: (t) => `translateX(-${getThemeValue(t, "space.s100")})`,
                      p: "s200",
                      mr: "s100",
                    }}
                  >
                    <ChevronNav
                      sx={{
                        width: 10,
                        height: "auto",
                        transform: isActive ? "rotateX(180deg)" : "none",
                        transition: "transform 0.2s ease-in-out",
                      }}
                    />
                  </IconButton>
                </Flex>
              )}
              {isActive && !navCategory.isStaticLink && (
                <NavDesktopCategoryDropdown
                  navCategory={selectedNavCategory}
                  buttonRef={tabRefs.current[index] as RefObject<HTMLButtonElement>}
                />
              )}
            </Flex>
          );
        })}
        <Flex as="li" role="none">
          <NavSearchButton />
        </Flex>
      </Container>
    </Box>
  );
};
